import { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import type { PluginManifest } from '@reflex/plugin-api';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@reflex/ui/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@reflex/ui/components/ui/scroll-area';
import { usePlugins } from '@/hooks/config/usePlugins';
import { PluginPreviewFrame } from './PluginPreviewFrame';

interface PluginPreviewsSectionProps {
  manifest: PluginManifest | undefined;
}

/**
 * Renders every opt-in marketing preview the plugin contributes. Each
 * preview is a self-contained component (with its own fake data) that
 * the plugin registered via `web.marketingPreviews`. Plugins without
 * any registered previews render nothing here.
 *
 * Single-preview plugins render the preview directly. Multi-preview
 * plugins get a horizontal tab strip (scrollable so 5+ surfaces don't
 * blow out the detail layout). Only the active preview is mounted so
 * switching tabs cleanly remounts each preview and avoids stale state.
 */
export function PluginPreviewsSection({ manifest }: PluginPreviewsSectionProps) {
  const { marketingPreviews } = usePlugins();

  const previews = useMemo(() => {
    if (!manifest) return [];
    return marketingPreviews.filter((p) => p.pluginName === manifest.name);
  }, [marketingPreviews, manifest]);

  // Track the user's tab preference. We derive the rendered id from this
  // at render time (falling back to the first preview) instead of syncing
  // through an effect — that way switching plugins in the detail sheet
  // automatically lands on a valid tab without an extra render pass.
  const [preferredId, setPreferredId] = useState<string | null>(null);

  if (previews.length === 0) return null;

  if (previews.length === 1) {
    const only = previews[0]!;
    const { id, label, description, surface, component: Component } = only;
    return (
      <SectionCard
        data-testid="plugin-previews-section"
        title="Preview"
        description="Sample views rendered with the real components and fake data. Useful for understanding what installing this plugin will look like in your workspace."
        icon={<Eye />}
        contentClassName="flex flex-col gap-4"
      >
        <PluginPreviewFrame
          key={id}
          label={label}
          description={description}
          surface={surface}
          testIdSuffix={id}
        >
          <Component />
        </PluginPreviewFrame>
      </SectionCard>
    );
  }

  const currentId =
    preferredId && previews.some((p) => p.id === preferredId) ? preferredId : previews[0]!.id;

  return (
    <SectionCard
      data-testid="plugin-previews-section"
      title="Preview"
      description="Sample views rendered with the real components and fake data. Useful for understanding what installing this plugin will look like in your workspace."
      icon={<Eye />}
      contentClassName="flex flex-col gap-4"
    >
      <Tabs value={currentId} onValueChange={setPreferredId} className="gap-3">
        <ScrollArea
          data-testid="plugin-previews-section-tabstrip"
          className="w-full whitespace-nowrap"
        >
          <TabsList variant="line" className="w-max gap-1">
            {previews.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                data-testid={`plugin-previews-section-tab-${p.id}`}
                className="gap-2"
              >
                <span>{p.label}</span>
                <span
                  data-testid={`plugin-previews-section-tab-${p.id}-surface`}
                  className="rounded bg-muted/60 px-1.5 py-0.5 text-3xs uppercase tracking-wider text-muted-foreground"
                >
                  {p.surface}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {previews
          .filter((p) => p.id === currentId)
          .map(({ id, label, description, surface, component: Component }) => (
            <TabsContent key={id} value={id} forceMount>
              <PluginPreviewFrame
                key={id}
                label={label}
                description={description}
                surface={surface}
                testIdSuffix={id}
              >
                <Component />
              </PluginPreviewFrame>
            </TabsContent>
          ))}
      </Tabs>
    </SectionCard>
  );
}
